// 'use client' is load-bearing (#118 slice 8): ProductCheckoutPage is hosted
// directly by the SERVER component app/[[...slug]]/RenderSectionsServer.jsx
// (ISLAND_SECTIONS), so it must declare its own client boundary. On the client
// RenderSections path it already runs inside the RegionPageClient island, where
// this directive is a no-op — same as the other hosted commerce islands.
'use client'

import React, { useEffect, useState } from 'react'
import styles from './styles.module.css'
import pairsWellWithStyles from './PairsWellWith/styles.module.css'
import { useServed } from '../../../../store/regionStore'
import { useRegionShippingInfo } from '../../../RegionGeo'
import { useCartStore, selectCart } from '../../../../store/cartStore'
import { useUiStore } from '../../../../store/uiStore'
import { useAddToCart } from '../../../hooks/useAddToCart'
import useProductOffers from '../../../../hooks/useProductOffers'
import { $neutralGray70, $yellow30 } from '../../../../styles/colors/primitives'
import Text from '../../../Text'
import Subtotal from './Subtotal'
import {
  calculateDiscountFromRegularPriceCents,
  calculatePriceForMultipleShopItems,
  isBundle,
  isInCart
} from '../../../../utils/shopItemsAndCart'
import CtaV2 from '../../../CtaV2'
import SecondaryNavbarAddToCart from './SecondaryNavbarAddToCart'
import { addCentsToPrice } from '../../../../utils/prices'
import CartIcon from '../../../icons/CartIcon'
import ItemSpecifications from '../Utils/ItemSpecifications'
import ProductInfoTags, { RIG_TRANSIT_TIME_DAYS } from './ProductInfoTags'
import Carousel from './Carousel'
import Offer from './Offer'
import {
  allItemsAreAvailable,
  findItemByType,
  getOptionalItems, isOutOfStock,
  isPreOrder,
  isUnavailableInRegion
} from '../../../../utils/shopItems'
import PairsWellWith from './PairsWellWith'
import { handleCheckbox } from '../../../../utils/arrays'
import Spacer from '../../../Spacer'
import CheckmarkIcon from '../../../icons/CheckmarkIcon'
import PreOrderWarningModal from './PreOrderWarningModal'
import { $preOrder } from '../../../../styles/colors/semantics'
import Description from './Description'
import DetailsUnderGallery from './DetailsUnderGallery'
import ProductGallery from '../Utils/ProductGallery'
import SimpleBlockContent from '../../../SimpleBlockContent'
import CantShipNotice from '../../../CantShipNotice'
import BundleSpecifications from '../Utils/ItemSpecifications/BundleSpecifications'
import Page404 from '../../Pages/Page404' // direct import, not the sections barrel (#130 de-barrel)
import ProductImages from './ProductImages'
import TimeOrStockLimitCountdownBF2025 from '../../../Utils/Countdown/ItemCountdownBF2025'
import WarningIcon from '../../../icons/WarningIcon'
import HolidaySaleTag from '../../../ShopItem/HolidaySaleTag'
import SaleProductBadge from '../../Sale/SaleProductBadge'
import { useUrlRegion } from '../../../../utils/useCurrentRegion'
import { getFulfilmentNotice } from '../../../../lib/fulfilmentNotices'
import { pushEcommerceEvent, buildEcommercePayload } from '../../../../utils/dataLayer'

const cx = (...c) => c.filter(Boolean).join(' ')

function ProductCheckoutPage({ itemType, fallbackItemType, itemOptionsText, title, showOptionWithAllItemsInSet, overrideDescription, importantInfo, sortByPriceDescending,
                               saleCampaign = null, saleNowMs = null, saleClockOffsetMs = 0 }) {
  // Cart line items now come from the Zustand cart store (Bucket C); the cart panel
  // + pre-order modal flags from the Zustand ui store (Phase C2 part 1). served /
  // offers stay on Overmind and are passed into the add-to-cart hook.
  const shoppingCart = useCartStore(selectCart)
  // seasonal sale badge (lib/saleCampaign): deal prices are region-specific
  const urlRegionForSale = useUrlRegion()
  const openCart = useUiStore((s) => s.openCart)
  const openPreOrderWarning = useUiStore((s) => s.openPreOrderWarning)
  const { checkoutPageAddToCart } = useAddToCart()
  // served + shippingInfo migrated off Overmind (Phase C2 part 2): served → the
  // Zustand region store selector; shippingInfo → the RegionGeo server-props seam.
  // offers (incl. shopItemsFetched) migrated off Overmind onto useProductOffers /
  // the offers store (Phase D1). Mixed file.
  const served = useServed()
  const shippingInfo = useRegionShippingInfo()

  // Shared product-checkout data logic (refetch gate + main-item resolution +
  // offer/gallery helpers) lives in useProductOffers so all four checkout forks
  // stay in lockstep (task #95). This page's per-page specifics — the combined
  // "all items in set" offer, the displayOnShop visibility rule, and optional
  // price-descending sort — are passed through unchanged.
  // `offers: shopItems` (the server-threaded / full-after-refetch offers array)
  // replaces the render-time `state.shopItems` reads below (byte-identical value
  // throughout). served + shippingInfo migrated off Overmind (Phase C2 part 2, see
  // above); state.shopItems (offers) stays on Overmind. Aliased to `shopItems` to
  // avoid colliding with this page's local `offers` (the buy-box offer list).
  const { item, getItemOffers, getOfferImages, offers: shopItems, shopItemsFetched } = useProductOffers({ itemType, fallbackItemType })

  const [selectedOfferIndex, setSelectedOfferIndex] = useState(0)
  const [selectedOptionalIndexes, setSelectedOptionalIndexes] = useState([])
  const [selectedVesaType, setSelectedVesaType] = useState(null)

  useEffect(() => {
   if (shopItemsFetched && offers && offers.length) {
     let preSelectedIndex = offers.findIndex(o => allItemsAreAvailable(o))
     setSelectedOfferIndex(preSelectedIndex >= 0 ? preSelectedIndex : 0)
   }
  }, [shopItemsFetched])

  // Monitor mounts (single/triple) get a VESA size dropdown instead of separate
  // shop cards: collect every size variant sharing the mount flag and treat the
  // selected one as the effective product (price / image / cart all follow it).
  // `item` is resolved by useProductOffers above (#95).
  const isMonitorMount = !!item && (item.isGT2SingleMonitorMount || item.isGT2TripleMonitorMount)
  const vesaVariants = isMonitorMount
    ? (shopItems || [])
        .filter(i => (item.isGT2SingleMonitorMount ? i.isGT2SingleMonitorMount : i.isGT2TripleMonitorMount))
        // Scope to THIS product's set. The mount flag alone groups every triple
        // stand together, so the TX32/TX43/TX60 sizes would land in one dropdown;
        // the VESA sizes of a single stand all share its productSetId. No-op for
        // the mounts that existed when the flag was the only grouping key.
        .filter(i => i.productSetId === item.productSetId)
        .slice()
        .sort((a, b) => vesaSizeValue(a) - vesaSizeValue(b))
    : []
  const effectiveMountItem = isMonitorMount ? (vesaVariants.find(v => v.type === selectedVesaType) || item) : null

  // Default the VESA selection to the page's own item type once it's available.
  useEffect(() => {
    if (isMonitorMount && item && !selectedVesaType) {
      setSelectedVesaType(item.type)
    }
  }, [isMonitorMount, item, selectedVesaType])

  useEffect(() => {
    if (item && item.id) {
      pushEcommerceEvent('view_item', buildEcommercePayload([item]))
    }
  }, [item && item.id])

  // Define offers early so useEffect can access it safely
  const offers = item
    ? (isMonitorMount && vesaVariants.length > 1
        ? [Array.of(effectiveMountItem)]
        : item.forceDisplayAsSingleItem
        ? [Array.of(item)]
        : getItemOffers(item, shopItems, { showOptionWithAllItemsInSet, requireDisplayOnShop: true, sortByPriceDescending }))
    : []

  // NOTE: Product JSON-LD is emitted once, server-side, from the page render
  // path (components/ProductStructuredData via [[...slug]].js) so it is region-
  // correct and not duplicated per section. Do not re-add a per-section block here.

  // Gate on `!shopItemsFetched` (grid-projected SSR payload makes this true,
  // so SSR renders the real checkout content — gallery/buy-box/price — NOT a
  // loader). The #127 `!mounted || !shopItemsFull` variant DID pin SSR ===
  // first-client-render and killed the #418/#422, but staging Lighthouse showed it
  // replaced the SSR content with a partial-height loader whose post-mount swap
  // regressed product-page CLS 0.05 -> 0.409 and LCP +2s — a user-visible loss
  // traded for a non-user-visible (quarantined) console error. Reverted to prod's
  // known-good SSR-content behavior; the #418 returns (re-quarantined) and the
  // PROPER fix — SSR-render the gallery with the FULL offers so there is neither a
  // loader swap NOR a mismatch — is task #90. (SpecsPage KEEPS its mounted-gate:
  // specs are never in SSR, so there it fixed a CLS 0.923 -> 0 swap.)
  if (!shopItemsFetched) {
    // CLS-by-construction (Phase 2 §1): render the loading spinner INSIDE the
    // real page layout with the gallery's box reserved (aspect-ratio 4/3, the
    // same box every ProductImages row uses), rather than a bare centered 100vh
    // spinner. This keeps the gallery region in the same place/size before and
    // after the client offers fetch paints the real gallery — so the spinner ->
    // content swap does not shift the gallery. (The gallery itself is still
    // painted client-side; SSR-rendering it is the separate structural task #90.)
    // The spinner is a deterministic CSS-only element — MUI's CircularProgress
    // (emotion) injects SSR-vs-client-divergent classes and was itself a
    // hydration-mismatch source (#127).
    return (
      <div className={styles.wrapper}>
        <div className={styles.contentWrapper}>
          <div className={styles.desktopCarousel}>
            <div className={styles.galleryLoadingBox} style={{ color: $neutralGray70 }}>
              <span className={styles.loadingSpinner} aria-label="Loading" role="status"/>
            </div>
          </div>
          <div className={styles.mobileCarousel}>
            <div className={styles.galleryLoadingBox} style={{ color: $neutralGray70 }}>
              <span className={styles.loadingSpinner} aria-label="Loading" role="status"/>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (!item) {
    return getItemNotFoundPage()
  }

  // Region-unavailable (e.g. an EU-only GT-2 rig viewed on /us or /au, #137): the
  // item IS present in the region's shopItems (the catalog join always includes
  // every catalog item) but carries no price for the resolved region, so a normal
  // render would leak a $0 / blank buy box (subtotal null, add-to-cart greyed with
  // no explanation). Route it to the SAME "not available for your location" state
  // as a missing item — no price, no buy button, a clear message + return-to-shop
  // CTA. Region + availability are fixed server-side, so SSR and the client agree
  // (no hydration mismatch). Detection is the shared cart check (isUnavailableInRegion).
  if (isUnavailableInRegion(item)) {
    return getItemNotFoundPage()
  }

  // main offer is the first item in list, it contains all items from product set
  const isMainOfferInCart = offers[0] && offers[0].every(i => isInCart(i, shoppingCart))
  const optionalItems = getOptionalItems(item, shopItems, false).filter(i => i.productSetId !== item.productSetId)
  const noOffersAvailable = !offers || !offers.length
  const taxIncluded = item && item.taxIncluded
  const description = item.fullSetDescription || item.description

  const images = getOfferImages(offers[selectedOfferIndex], item)

  const getSelectedItems = () => {
    if (!offers || !offers.length) {
      return []
    }

    if (isMainOfferInCart) {
      // if main item is already added in cart
      // selected items are - main item + all optional items in cart, so subtotal can be calculated correctly
      return Array.from(offers[0]).concat(optionalItems.filter(i => isInCart(i, shoppingCart)))
    }

    const selectedMainOffer = offers[selectedOfferIndex]
    const selectedOptionalItems = Array.from(new Set(selectedOptionalIndexes)).flatMap(id => optionalItems[id])

    return Array.from(selectedMainOffer).concat(selectedOptionalItems).filter(o => !!o)
  }

  const getPreOrderInfo = () => {
    if (allItemsAreAvailable(getSelectedItems()) && selectionIsPreOrder(getSelectedItems())) {
      return (
        <div className={styles.preOrderWrapper}>
          <Text type="heading-6">Pre-Order Now: Be the First to Get It!</Text>
          <Text type="regular">Our latest product is here for pre-order! Shipping is expected after the holiday season as we perfect every detail.</Text>
          <Text type="heading-8">Pre-order now to:</Text>
          <Text type="regular">•  Secure Your Spot before the rush.</Text>
          <Text type="regular">•  Get Updates on shipping timelines.</Text>
          <Text type="heading-8">Estimated delivery:</Text>
          <Text type="regular">•  after Christmas and New Year.</Text>
          <Text type="regular">Don’t miss out — order today and start the new year with something amazing!</Text>
        </div>
      )
    }
  }

  const getAddToCartCta = (isMainCta) => {
    const preOrderItem = getSelectedItems().find(i => i.preOrderInfo && i.preOrderInfo.isPreOrderAvailable)
    const isPreOrder = !!preOrderItem

    return {
      title: isMainOfferInCart ? 'Item added' : (isPreOrder ? 'Pre-order' : 'Add to cart'),
      titleIcon: isMainOfferInCart ? <CheckmarkIcon/> : (isMainCta ? <CartIcon large/> : <CartIcon/>),
      light: isMainOfferInCart,
      sx: {
        height: isMainCta ? '80px' : null,
        titleTextType: isMainCta ? 'large' : null,
        background: isPreOrder ? $preOrder : null,
        width: isMainCta ? '100%' : null,
      },
      // served=false (we can't ship to the visitor's country): block the buy
      // action entirely — the CantShipNotice replaces it with a clear message.
      disabled: !served || !getSelectedItems().length || !allItemsAreAvailable(getSelectedItems()),
      onClick: () => {
        if (!served) {
          return
        }
        if (isMainOfferInCart) {
          openCart()
        } else if (isPreOrder) {
          openPreOrderWarning({ ...preOrderItem.preOrderInfo })
        } else {
          onAddToCartClick()
        }
      }
    }
  }

  const onAddToCartClick = () => checkoutPageAddToCart(getSelectedItems().map(i => i.id))

  // Region-specific fulfilment notice for the selected option (e.g. AU pedals
  // delivery time), shown in yellow next to the Add to cart button.
  const fulfilmentNotice = getFulfilmentNotice(urlRegionForSale, getSelectedItems())

  const getSubtotal = () => {
    return calculatePriceForMultipleShopItems(getSelectedItems())
  }

  const getSubtotalDiscountedCents = () => {
    return calculateDiscountFromRegularPriceCents(getSelectedItems())
  }

  return (
    <>
      <SecondaryNavbarAddToCart
        title={title}
        subtotal={getSubtotal()}
        subtotalBeforeDiscount={addCentsToPrice(getSubtotal(), getSubtotalDiscountedCents())}
        ctaV2={getAddToCartCta(false)}
      />
      <PreOrderWarningModal onAddToCart={onAddToCartClick}/>
      <div className={styles.wrapper}>
        <div className={cx(styles.contentWrapper, images.length > 1 && styles.contentWrapperWide)}>
          <div className={styles.desktopCarousel}>
            {/* Products with several photos opt into the arrow + thumbnail
                gallery (the GT-2 configurator's) instead of the stacked
                photo-mosaic, which pushes the copy below it far down the page. */}
            { (isMonitorMount ? effectiveMountItem : item)?.carouselGallery
              ? <ProductGallery images={images} aspectRatio={galleryAspectRatio(images)}/>
              : <ProductImages images={images}/> }
          </div>
          <div className={styles.productInfo}>
            <div className={styles.productText}>
              <div className={styles.productTitle}>
                {
                  item.category.displayName === 'Holiday sale' && <div className="mb-2"><HolidaySaleTag show={true}/></div>
                }
                <Text type="heading-10">VRS® DirectForce®</Text>
                <Text type="heading-3" primary highlight="G2" highlightSx="color: #E11D48;" highlightExactMatch>{isMonitorMount ? effectiveMountItem.name : (title || item.name)}</Text>
              </div>
              <Description overrideDescription={overrideDescription} description={description}/>
              { importantInfo && <SimpleBlockContent blocks={importantInfo}/> }
            </div>
            <div className={styles.mobileCarousel}><Carousel images={images}/></div>
            <div className={styles.checkout}>
              { isMonitorMount && vesaVariants.length > 1 && (
                <div className={styles.vesaSelectWrapper}>
                  <Text type="heading-8">Monitor mount VESA size</Text>
                  <select
                    className={styles.vesaSelect}
                    value={effectiveMountItem?.type || ''}
                    onChange={(e) => setSelectedVesaType(e.target.value)}
                  >
                    {vesaVariants.map((v) => (
                      <option key={v.type} value={v.type}>
                        VESA {getVesaSize(v)}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              { (() => {
                if (noOffersAvailable) {
                  return <CtaV2
                    title="Get notified"
                    onClick={() => window.open('https://app.vrs.racing/#/Store', '_blank')}
                    fullWidth
                    light={true}
                  />
                } else if (offers.length === 1) {
                  return null
                } else {
                  return (
                    <div className={styles.offers}>
                      { itemOptionsText && <><Text type="heading-8">{itemOptionsText}</Text><Spacer size={16}/></> }
                      {
                        offers.map((o, i) => <Offer
                          key={`offer-${i}`}
                          items={o}
                          isOutOfStock={!allItemsAreAvailable(o)}
                          isNew={!!o.some(i => i.isNew)}
                          isSelected={selectedOfferIndex === i}
                          onClick={() => {
                            setSelectedOfferIndex(i)
                          }}
                        />)
                      }
                    </div>
                  )
                }
              }) ()
              }
              {/* sale deal / countdown for the selected option, right under the
                  option prices it refers to */}
              {!noOffersAvailable && (
                <SaleProductBadge campaign={saleCampaign} saleNowMs={saleNowMs} saleClockOffsetMs={saleClockOffsetMs} region={urlRegionForSale} items={offers[selectedOfferIndex] || (item ? [item] : [])}/>
              )}
              <div className={cx(pairsWellWithStyles.pairsWellWithWrapper, !(optionalItems && optionalItems.length) && pairsWellWithStyles.pairsWellWithWrapperHidden)}>
                <div>
                  <Text type="heading-9">Pairs well with</Text>
                  <Spacer size={4}/>
                </div>
                {
                  optionalItems.map((o, i) => <PairsWellWith
                      key={o.name}
                      item={o}
                      isItemInCart={isInCart(o, shoppingCart)}
                      isMainOfferInCart={isMainOfferInCart}
                      onCheckboxClick={() => handleCheckbox(selectedOptionalIndexes, i, (list) => setSelectedOptionalIndexes(list))}
                      isSelected={selectedOptionalIndexes.includes(i)}
                      isAvailable={!isOutOfStock(o)}
                    />
                  )}
              </div>
              {
                noOffersAvailable
                  ? null
                  : (
                    <div className={styles.subtotalAndPreOrder}>
                      <CantShipNotice/>
                      <Subtotal
                        subtotal={getSubtotal()}
                        discountCents={getSubtotalDiscountedCents()}
                        taxIncluded={item && item.taxIncluded}
                        cta={getAddToCartCta(true)}
                      />
                      { fulfilmentNotice && (
                        <div className="flex gap-1 items-center bg-yellow-100 rounded-lg p-1" data-testid="fulfilment-notice">
                          <WarningIcon color={$yellow30}/>
                          <Text type="heading-9">{fulfilmentNotice.message}</Text>
                        </div>
                      )}
                      { getPreOrderInfo() }
                      {
                        item.category.displayName === 'Black Friday Deals' && (item.timeLimit || item.stockLimit > 0)
                        && <TimeOrStockLimitCountdownBF2025 launchDate={item.timeLimit && new Date(item.timeLimit)} stockLimit={item.stockLimit} stockLimitLeft={item.stockLimitLeft} theme="light"/>
                      }
                    </div>
              )}
              { !!item && !!item.importantMessages && !!item.importantMessages.length && item.importantMessages.map((m, i)=> <div className="flex gap-1 items-center bg-yellow-100 rounded-lg p-1" key={m + '-' + i}>
                  <WarningIcon color={$yellow30}/>
                  <Text type="heading-9">{m}</Text>
                </div>
              )}
              <ProductInfoTags transitTitle={fulfilmentNotice && fulfilmentNotice.transitTime} transitTimeDays={selectionIsPreOrder(getSelectedItems()) ? null : (isRigItem(item) ? RIG_TRANSIT_TIME_DAYS : shippingInfo.transitTimeDays)}/>
              { getSpecs(item, shopItems) }
            </div>
          </div>
        </div>
      </div>
      {/* Products with a full spec sheet repeat it under the gallery, unclamped
          and uncollapsed (opt-in per catalog doc). */}
      <DetailsUnderGallery item={isMonitorMount ? effectiveMountItem : item}/>
    </>
  )
}

// Shape the carousel frame to the photos themselves. The catalog descriptors
// carry their source dimensions, and these products' renders are 2:1 — left at
// the gallery's default square the image would sit in a frame twice as tall as
// itself, the empty half pushing the copy below it off the screen.
const galleryAspectRatio = (images) => {
  const first = images && images[0]
  return first && first.width && first.height ? `${first.width} / ${first.height}` : undefined
}

// Parse the VESA pattern ("100x100" / "200x200") out of a monitor-mount item's
// type (or name) for the size dropdown, mirroring the GT2 configurator.
const getVesaSize = (item) => {
  const m = item?.type?.match(/(\d+x\d+)$/) || item?.name?.match(/(\d+x\d+)/)
  return m ? m[1] : null
}
const vesaSizeValue = (item) => {
  const s = getVesaSize(item)
  return s ? parseInt(s, 10) : 0
}

// The Sim Rigs category covers the cockpits, the seat and the mounts. The
// GT2_COCKPIT prefix additionally catches rig BUNDLES, which are filed under
// Bundles but ship out of the factory like the rig they contain.
const isRigItem = (item) => !!item && (
  (!!item.category && item.category.type === 'SIM_RIGS')
  || String(item.type || item.itemType || '').startsWith('GT2_COCKPIT')
)

const selectionIsPreOrder = (selectedItems) => {
  return selectedItems.some(i => isPreOrder(i))
}

const getItemNotFoundPage = () => {
  return <Page404 titleTextOverride={ <Text type="heading-2">Item not available for your location.</Text> }
           ctasV2={[
             { title: 'Return to shop', routeOrUrl: { url: '/shop' } },
             { title: 'Contact support', routeOrUrl: { url: '/contact' }, dark: true },
           ]}
  />
}

const getSpecs = (item, shopItems) => {
  return (
    <div className={styles.specsWrapper}>
      {
        isBundle(item) && !item.specs
        ? <BundleSpecifications items={item.bundledItemTypes.map(i => findItemByType(i, shopItems))} theme={'light'}/>
        : <ItemSpecifications specs={item.specs} theme={'light'}/>
      }
    </div>
  )
}

export default ProductCheckoutPage
